<script>
/**
 *
 * Usage Guide:
 *
 * My Component
 * ```
 * import FormGenerator from '@/components/common/FormGenerator'
 *
 * data() {
 *   let field1 = {type: 'text', default: 'Default Value', options: {label: 'First Name'}, halfWidth: true}
 *   let field2 = {type: 'text', default: 'Default Value', options: {label: 'First Name'}}
 *   let field3 = {type: 'array', default: [{a: 'a', b: 'b'}, {}, {}], options: {label: 'Deep Structure'}}
 *   let schema = [field1, field2]
 *
 *   return {
 *     schema
 *   }
 * }
 *
 * <form-generator :schema="schema" :isreadonly="false" @submit="onSubmit" @cancel="onCancel"></form-generator>
 *
 *
 */

import {
  VCheckbox,
  VSelect,
  VSlider,
  VTextarea,
  VTextField,
  VDatePicker,
  VTimePicker
} from 'vuetify/lib'
import _ from 'lodash'
import moment from 'moment'
import FileInput from '@/components/common/form-generator/FileInput'
import TableInput from '@/components/common/form-generator/TableInput'
import { TreeView } from 'vue-json-tree-view'
import UserApproval from '@/components/honey-form/UserApproval'
import UserApprovalHeader from '../../honey-form/UserApprovalHeader'
import FileAttachment from './FileAttachment'

// map to hold field-type-to-vuetify-form-type mappings.
const componentMap = {
  text: VTextField,
  textarea: VTextarea,
  select: VSelect,
  slider: VSlider,
  checkbox: VCheckbox,
  file: FileInput,
  array: TableInput,
  object: TreeView,
  date: VDatePicker,
  time: VTimePicker,
  approval: UserApproval,
  approvalHeader: UserApprovalHeader,
  fileAttachments: FileAttachment
}

const isSinglefield = {
  text: true,
  textarea: true,
  select: true,
  slider: true,
  checkbox: true,
  file: false,
  array: false,
  object: false,
  date: true,
  time: true,
  approval: false,
  approvalHeader: false,
  fileAttachments: false
}

function getDefault(f) {
  let defaultArray, defaultObject, defaultItem
  switch (f.type) {
    case 'date' || 'time':
      return f.type === 'date' || f.type === 'time'
        ? null
        : f.options.value || f.default
    case 'array' || 'list':
      defaultArray = f.options.value || f.default
      if (_.isArray(defaultArray)) {
        return defaultArray
      } else {
        return []
      }
    case 'object':
      defaultObject = f.options.value || f.default
      if (_.isObject(defaultObject)) {
        return defaultObject
      } else {
        return {}
      }
    case 'select':
      defaultItem = f.options.value || f.default
      if (_.isArray(defaultItem) && defaultItem.length > 0) {
        return defaultItem[0]
      } else {
        return defaultItem
      }
    case 'file':
      defaultItem = f.options.value || f.default
      if (_.isArray(defaultItem)) {
        return defaultItem
      } else {
        return []
      }
    default:
      return f.options.value || f.default
  }
}

// util to fill up model values from schema
const schema2model = function(schema) {
  let model = {}
  schema.forEach(f => {
    model[f.key] = getDefault(f)
  })
  return model
}

// util to rebind rules
const passModelToRules = function(schema, model) {
  let self = this
  return schema.forEach(f => {
    let rules = f.options.rules
    if (!rules) return false
    f.options.rules = rules.map(r => {
      return r.bind(self, model)
    })
  })
}

const getFieldsFromSchema = function(schema) {
  return schema.map(f => {
    return {
      ...f,
      component: componentMap[f.type]
    }
  })
}

export default {
  name: 'form-generator',
  props: {
    submitTitle: String,
    schema: {
      type: Array,
      default: () => [],
      required: true
    },
    readonly: Boolean,
    autoSync: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      default: () => {}
    },
    canSubmit: {
      type: Boolean,
      default: true
    },
    cancel: {
      type: Boolean,
      default: true
    },
    submitLoader: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      model: {},
      showForm: false,
      validForm: true,
      rules: {
        required: value => !!value || 'Required Input'
      },
      fields: []
    }
  },

  watch: {
    model() {
      this.$emit('input', this.model)
    },
    schema() {
      // Fill up this.model using schema.default || schema.value
      this.model = schema2model(this.schema)
      // re-bind rules for passing down model to rule callbacks
      passModelToRules(this.schema, this.model)
      // get fields
      this.fields = getFieldsFromSchema(this.schema)
    }
  },

  methods: {
    reset: function() {
      let self = this
      this.schema.forEach(f => {
        self.model[f.key] = f.options.value || f.default
      })
    },

    updateFileLink: function(link, key) {
      this.model[key] = link
    },

    getItemSchema(item) {
      // return a list of property fields
      if (item.options.deepStructure) {
        let fields = item.options.deepStructure.map(field => {
          let f = {
            ...field
          }

          return f
        }, this)
        return fields
      } else {
        return this.getFieldsForDisplay(item.options.value)
      }
    },

    checkArray(value) {
      return _.isArray(value) && !_.isNull(value) && !_.isUndefined(value)
    },

    checkObject(value) {
      return _.isObject(value) && !_.isNull(value) && !_.isUndefined(value)
    },

    getDefaultsForDisplay(object) {
      let itemArray = []
      for (let [key, value] of Object.entries(object)) {
        if (object.hasOwnProperty(key)) {
          itemArray.push(value)
        }
      }
      return itemArray
    },

    getFieldsForDisplay(object) {
      let itemArray = []
      if (!_.isUndefined(object) && !_.isNull(object)) {
        for (let [key, value] of Object.entries(object)) {
          if (object.hasOwnProperty(key)) {
            let formItem = {}
            formItem.key = key
            formItem.options = {}
            formItem.options.cssClass = 'mx-1 my-1'
            formItem.options.label = key

            if (_.isArray(value)) {
              formItem.type = 'array'
              let tableItems = []
              value.map(i => {
                tableItems.push(this.getDefaultsForDisplay(i))
              })
              formItem.default = tableItems
              formItem.options.deepStructure =
                value.length > 0 ? this.getFieldsForDisplay(value[0]) : []
            } else if (_.isString(value)) {
              formItem.type = 'text'
              formItem.default = value
            } else if (_.isUndefined(value)) {
              formItem.type = 'text'
              formItem.default = ''
            } else if (_.isNull(value)) {
              formItem.type = 'text'
              formItem.default = ''
            } else if (_.isObject(value)) {
              formItem.type = 'object'
              formItem.default = this.getFieldsForDisplay(value)
              formItem.options.deepStructure = this.getFieldsForDisplay(value)
            } else {
              formItem.type = 'text'
              formItem.default = value
            }
            itemArray.push(formItem)
          }
        }
      }
      return itemArray
    },

    onSubmit: function() {
      // validate required
      const isValid = this.fieldsValidator()
      if (
        isValid &&
        (this.$refs.proposalForm === undefined ||
          this.$refs.proposalForm.validate())
      ) {
        this.$emit('submit', this.model)
      }
    },

    fieldsValidator: function() {
      var isValid = true
      var missingFieldLabel

      for (var field of this.fields) {
        if (field.required) {
          // check whether string or array
          if (isSinglefield[_.toLower(field.type)]) {
            if (
              this.model[field.key] == '' ||
              this.model[field.key] == null ||
              this.model[field.key] == undefined
            ) {
              missingFieldLabel = field.options.label
              isValid = false
              break
            }
          } else {
            if (this.model[field.key].length == 0) {
              missingFieldLabel = field.options.label
              isValid = false
              break
            }
          }
        }
      }
      if (!isValid) {
        // show error alert
        this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Error Some Fields Are Missing',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a'),
          description: missingFieldLabel + ' is required'
        })
      }

      return isValid
    },

    onCancel: function() {
      this.reset()
      this.$emit('cancel')
    },
    updateChildFormModel: function(item, key) {
      this.model[key] = item
    }
  },

  mounted() {
    this.$emit('input', this.model)
    this.model = schema2model(this.schema)
    // re-bind rules for passing down model to rule callbacks
    passModelToRules(this.schema, this.model)
    // get fields
    this.fields = getFieldsFromSchema(this.schema)
  },

  components: {
    UserApprovalHeader,
    FileInput,
    TableInput,
    UserApproval,
    FileAttachment
  }
}
</script>
<template>
  <div>
    <v-layout row wrap>
      <v-flex xs12 v-if="fields && fields.length > 0">
        <v-form ref="proposalForm" v-model="validForm" v-on:submit.prevent>
          <v-container fluid>
            <v-layout align-center justify-space-between row fill-height wrap>
              <template v-for="(item, i) in fields">
                <slot :name="item.type" :field="item">
                  <!-- CAPTCHA -->
                  <v-flex
                    :class="item.options.cssClass"
                    v-if="item.type === 'captcha'"
                    :key="'flex0' + i"
                  >
                    <v-card
                      style="border-radius: 10px"
                      flat
                      :class="$vuetify.dark ? 'card-dark ' : 'card-light '"
                    >
                      <v-toolbar dense flat class="beez-bold">
                        Captcha Confirmation
                      </v-toolbar>
                      <v-card-text>
                        <v-layout
                          align-center
                          justify-space-between
                          row
                          fill-height
                          wrap
                        >
                          <v-flex xs12 md6 lg4 class="pa-3">
                            <v-img
                              :src="item.options.additionalInfo.imgUrl"
                              alt="Captcha Image"
                              contain
                            ></v-img>
                          </v-flex>
                          <v-flex xs12 md6 lg4>
                            <v-text-field
                              :key="i + 'capcha-input'"
                              v-model="model[item.key]"
                              :readonly="readonly"
                              :rules="item.required ? [rules.required] : []"
                              :required="item.required"
                              v-bind="item.options"
                            >
                            </v-text-field>
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </v-flex>
                  <!-- file input -->
                  <v-flex
                    :class="item.options.cssClass"
                    v-if="item.type === 'file'"
                    :key="'flex1' + i"
                  >
                    <v-card
                      style="border-radius: 10px"
                      flat
                      :class="$vuetify.dark ? 'card-dark ' : 'card-light '"
                    >
                      <v-toolbar dense flat class="beez-bold">
                        Upload File
                      </v-toolbar>
                      <v-card-text>
                        <v-layout
                          align-center
                          justify-space-between
                          row
                          fill-height
                          wrap
                        >
                          <v-flex xs12 md4>
                            <p
                              :key="i + 'comp-file-label'"
                              class="text-left subheading"
                            >
                              {{ item.options.label }}
                            </p>
                            <p
                              :key="i + 'comp-file-sublabel'"
                              class="text-left body-2"
                            >
                              {{ item.options.hint }}
                            </p>
                          </v-flex>
                          <v-flex xs12 md8>
                            <file-input
                              :links="model[item.key]"
                              @filepick="updateFileLink"
                              :modelkey="item.key"
                              :key="i + 'comp-file-input'"
                              :options="item.options"
                            />
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </v-flex>
                  <!-- USER APPROVAL -->
                  <v-flex
                    :key="'flex2' + i"
                    class="xs12"
                    v-else-if="item.type === 'approvalheader'"
                  >
                    <user-approval-header
                      :approval-header="model[item.key]"
                      :key="i + 'comp-user-approval-header'"
                    />
                  </v-flex>
                  <!-- FILE ATTACHMENTS -->
                  <v-flex
                    :key="'flex2' + i"
                    class="xs12"
                    v-else-if="item.type === 'fileattachments'"
                  >
                    <file-attachment
                      :input="model[item.key]"
                      :key="i + 'comp-file-attachment'"
                    />
                  </v-flex>
                  <!-- APPROVAL -->
                  <v-flex
                    :key="'flex3' + i"
                    class="xs12"
                    v-else-if="item.type === 'approval'"
                  >
                    <user-approval
                      v-model="model[item.key]"
                      :read-only="readonly"
                      :key="i + 'comp-user-approval'"
                    />
                  </v-flex>
                  <!-- SELECT -->
                  <v-flex
                    :key="'flex4' + i"
                    :class="item.options.cssClass"
                    v-else-if="item.type === 'select'"
                  >
                    <v-select
                      :key="i + 'comp-vuetify-component'"
                      v-model="model[item.key]"
                      :multiple="
                        item.options.selectProps
                          ? item.options.selectProps.multiple
                          : false
                      "
                      :items="
                        item.options.selectProps &&
                        item.options.selectProps.childSelect
                          ? item.items.filter(
                              a =>
                                a.groupName ===
                                model[item.options.selectProps.parentSelectKey]
                            )
                          : item.items
                      "
                      :rules="item.required ? [rules.required] : []"
                      :item-text="
                        item.options.selectProps
                          ? item.options.selectProps.text
                          : null
                      "
                      :item-value="
                        item.options.selectProps
                          ? item.options.selectProps.value
                          : null
                      "
                      :required="item.required"
                      :readonly="readonly"
                      v-bind="item.options"
                    />
                  </v-flex>
                  <!-- Table input -->
                  <v-flex
                    :class="item.options.cssClass"
                    v-else-if="
                      checkArray(model[item.key]) && item.type !== 'object'
                    "
                    :key="'flex5' + i"
                  >
                    <table-input
                      v-model="model[item.key]"
                      :default="checkArray(item.default) ? item.default : []"
                      :options="item.options"
                      :readonly="readonly"
                    />
                  </v-flex>
                  <!-- Date input -->
                  <v-flex
                    :class="item.options.cssClass"
                    v-else-if="item.type === 'date'"
                    :key="'flex6' + i"
                  >
                    <v-layout row wrap>
                      <v-flex xs12 md4>
                        <p
                          :key="i + 'comp-date-label'"
                          class="text-capitalize text-left subheading"
                        >
                          {{ item.options.label }}
                        </p>
                        <p
                          :key="i + 'comp-date-sublabel'"
                          class="text-capitalize text-left body-2"
                        >
                          {{ item.options.hint }}
                        </p>
                      </v-flex>
                      <v-flex xs12 md8>
                        <v-date-picker
                          :key="i + 'comp-date-picker'"
                          v-model="model[item.key]"
                          :rules="item.required ? [rules.required] : []"
                          :required="item.required"
                          landscape
                          full-width
                        />
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <!-- TIME INPUT -->
                  <v-flex
                    :class="item.options.cssClass"
                    v-else-if="item.type === 'time'"
                    :key="'flex7' + i"
                  >
                    <v-layout row wrap>
                      <v-flex xs12 md4>
                        <p
                          :key="i + 'comp-time-label'"
                          class="text-capitalize text-left subheading"
                        >
                          {{ item.options.label }}
                        </p>
                        <p
                          :key="i + 'comp-time-sublabel'"
                          class="text-capitalize text-left body-2"
                        >
                          {{ item.options.hint }}
                        </p>
                      </v-flex>
                      <v-flex xs12 md8>
                        <v-time-picker
                          :key="i + 'comp-time-picker'"
                          v-model="model[item.key]"
                          :rules="item.required ? [rules.required] : []"
                          :required="item.required"
                          landscape
                          full-width
                        />
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <!-- OBJECT INPUT -->
                  <v-flex
                    :class="item.options.cssClass"
                    :key="'flex8' + i"
                    v-else-if="
                      item.type === 'object' ||
                        typeof model[item.key] === 'object' ||
                        checkObject(model[item.key])
                    "
                    class="text-xs-left horizontal-scroll"
                  >
                    <v-card>
                      <v-toolbar
                        dense
                        card
                        class="elevation-2"
                        @click="showForm = !showForm"
                      >
                        <span class="beez-bold text-capitalize"
                          >Form Input | {{ item.options.label }}</span
                        >
                        <v-spacer />
                        <v-btn icon small class="mb-2">
                          <v-icon
                            >{{
                              showForm
                                ? 'keyboard_arrow_up'
                                : 'keyboard_arrow_down'
                            }}
                          </v-icon>
                        </v-btn>
                      </v-toolbar>
                      <v-card-text v-if="showForm">
                        <v-flex xs12 sm12 md12>
                          <form-generator
                            :key="i + 'comp-object-form'"
                            :schema="getItemSchema(item)"
                            :readonly="readonly"
                            @submit="updateChildFormModel($event, item.key)"
                          />
                        </v-flex>
                      </v-card-text>
                    </v-card>
                  </v-flex>
                  <!-- Other input components -->
                  <v-flex
                    :class="item.options.cssClass"
                    v-else
                    :key="'flex9' + i"
                    grow
                  >
                    <component
                      :key="i + 'comp-vuetify-component'"
                      v-model="model[item.key]"
                      :readonly="readonly"
                      :rules="item.required ? [rules.required] : []"
                      :required="item.required"
                      :is="item.component"
                      v-bind="item.options"
                    />
                  </v-flex>
                </slot>
              </template>
            </v-layout>
          </v-container>
        </v-form>
      </v-flex>
      <v-flex xs12 v-else>
        <v-alert
          :value="true"
          color="warning"
          icon="info"
          :outline="$vuetify.dark"
          style="border-radius: 5px"
        >
          {{
            readonly
              ? 'No form elements found'
              : 'Press Submit button to proceed'
          }}
        </v-alert>
      </v-flex>
    </v-layout>
    <v-layout row v-if="!readonly && !autoSync" class="pb-5">
      <v-flex xs12>
        <v-layout justify-center align-center>
          <v-btn
            v-if="cancel"
            class="text-capitalize"
            @click="onCancel"
            :outline="$vuetify.dark"
          >
            Cancel
          </v-btn>
          <v-btn
            v-if="canSubmit"
            :outline="$vuetify.dark"
            @click="onSubmit"
            :disabled="!validForm"
            :loading="submitLoader"
            :class="
              $vuetify.dark
                ? 'secondary text-capitalize'
                : 'primary text-capitalize'
            "
          >
            {{ submitTitle || 'Submit' }}
          </v-btn>
        </v-layout>
      </v-flex>
    </v-layout>
  </div>
</template>
<style scoped>
.invert-data-table-light {
  background-color: #eaeaea !important;
}

.invert-data-table-dark {
  background-color: #212121 !important;
}

>>> .v-text-field__slot {
  caret-color: #a5a5a5 !important;
}

>>> .v-label--active {
  color: #a5a5a5 !important;
}
</style>
